/**
 * Picker — Apple-style dropdown select.
 *
 * Maps to SwiftUI Picker with .menu style.
 * Floating label sits inside the trigger, floats up small when a value is selected.
 * Portal-based dropdown that escapes overflow containers.
 * Smart positioning (opens up or down based on available space).
 *
 * Usage:
 *   <Picker
 *     label="Address Type"
 *     value={type}
 *     onChange={setType}
 *     options={[{ value: 'home', label: 'Home' }, { value: 'work', label: 'Work' }]}
 *   />
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ds } from '../tokens';

export interface PickerOption {
  value: string;
  label: string;
}

export interface PickerProps {
  label?: string;
  value?: string;
  onChange: (value: string) => void;
  options: PickerOption[];
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  searchable?: boolean;
  /** Compact mode — smaller padding and text for inline/row usage */
  compact?: boolean;
  /** Auto-open the dropdown (e.g., to draw user attention) */
  autoOpen?: boolean;
  className?: string;
}

export function Picker({
  label,
  value,
  onChange,
  options,
  placeholder = 'Select',
  disabled = false,
  required: _required = false,
  error,
  searchable = false,
  compact = false,
  autoOpen = false,
  className = '',
}: PickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0, openUp: false });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find(o => o.value === value);
  const isFloated = isOpen || !!selectedOption;

  const filteredOptions = searchable && search
    ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  // Calculate position and open
  const open = useCallback(() => {
    if (disabled || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const maxHeight = 240;
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const openUp = spaceBelow < maxHeight && spaceAbove > spaceBelow;

    setPosition({ top: openUp ? rect.top : rect.bottom + 4, left: rect.left, width: rect.width, openUp });
    setIsOpen(true);
    setSearch('');
  }, [disabled]);

  // Auto-open when requested
  useEffect(() => {
    if (autoOpen && !isOpen) {
      // Small delay to let the DOM render the trigger button
      const timer = setTimeout(() => open(), 100);
      return () => clearTimeout(timer);
    }
  }, [autoOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on outside click or Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (triggerRef.current?.contains(e.target as Node) || dropdownRef.current?.contains(e.target as Node)) return;
      setIsOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation();
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    // Capture phase so we intercept before EntitySheet / other modal handlers
    document.addEventListener('keydown', handleKey, true);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey, true);
    };
  }, [isOpen]);

  // Auto-focus search on open
  useEffect(() => {
    if (isOpen && searchable) {
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [isOpen, searchable]);

  const handleSelect = (val: string) => {
    onChange(val);
    setIsOpen(false);
  };

  // Border and ring
  const borderColor = error
    ? ds.color.state.errorBorder
    : isOpen
      ? ds.color.state.focusBorder
      : ds.color.separator.field;

  const ringClass = isOpen ? ds.color.state.focusRing : '';

  // Dropdown position — compact mode: auto-fit to content, at least as wide as trigger
  const dropdownStyle: React.CSSProperties = {
    top: position.openUp ? undefined : position.top,
    bottom: position.openUp ? window.innerHeight - position.top + 4 : undefined,
    left: position.left,
    width: compact ? 'fit-content' : position.width,
    minWidth: position.width,
    maxHeight: 240,
  };

  return (
    <div className={className}>
      {/* Trigger button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => isOpen ? setIsOpen(false) : open()}
        disabled={disabled}
        className={[
          'relative w-full text-left',
          compact ? 'px-2 py-1.5' : 'px-3',
          !compact && (label ? 'pt-6 pb-1.5' : 'py-2.5'),
          ds.color.fill.primary,
          ds.radius.md,
          'border',
          borderColor,
          ringClass,
          ds.transition.fast,
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
        ].filter(Boolean).join(' ')}
      >
        {/* Floating label */}
        {label && (
          <span
            className={[
              'absolute left-3 pointer-events-none origin-top-left',
              'transition-all duration-150 ease-out',
              isFloated
                ? [
                    'top-1.5 scale-[0.75]',
                    ds.color.label.floated,
                  ].join(' ')
                : [
                    'top-1/2 -translate-y-1/2',
                    ds.type.body,
                    ds.color.label.tertiary,
                  ].join(' '),
            ].join(' ')}
          >
            {label}
          </span>
        )}

        {/* Selected value (hidden when label acts as placeholder) */}
        <span
          className={[
            `block ${compact ? 'pr-4' : 'pr-6'} truncate`,
            compact ? ds.type.footnoteLg : ds.type.body,
            ds.type.weight.medium,
            isFloated || !label
              ? selectedOption ? ds.color.label.primary : ds.color.label.tertiary
              : 'text-transparent',
          ].join(' ')}
        >
          {selectedOption?.label || placeholder}
        </span>

        {/* Chevron (up-down per Apple Picker spec) */}
        <svg
          className={[
            `absolute ${compact ? 'right-1.5' : 'right-3'} top-1/2 -translate-y-1/2`,
            compact ? ds.icon.xs : ds.icon.sm,
            ds.color.label.secondary,
            ds.transition.fast,
            isOpen ? 'rotate-180' : '',
          ].join(' ')}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Error message */}
      {error && (
        <p className={[ds.type.caption1, ds.color.label.destructive, 'mt-1 px-1'].join(' ')}>{error}</p>
      )}

      {/* Portal dropdown */}
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className={[
            'fixed overflow-hidden',
            ds.zIndex.dropdown,
            ds.color.fill.primary,
            ds.radius.md,
            'border',
            ds.color.separator.light,
            ds.shadow.menu,
          ].join(' ')}
          style={dropdownStyle}
        >
          {/* Search filter */}
          {searchable && (
            <div className={['p-2 border-b', ds.color.separator.default].join(' ')}>
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search..."
                className={[
                  'w-full px-2.5 py-1.5 outline-none',
                  ds.type.body,
                  ds.color.label.primary,
                  ds.color.label.placeholder,
                  ds.color.fill.secondary,
                  ds.radius.sm,
                ].join(' ')}
              />
            </div>
          )}

          {/* Options list */}
          <div className={`overflow-y-auto px-1.5 py-1.5 space-y-0.5 ${ds.layout.scrollbar}`} style={{ maxHeight: searchable ? 192 : 240 }}>
            {filteredOptions.map(option => {
              const isSelected = option.value === value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelect(option.value)}
                  className={[
                    `w-full text-left flex items-center justify-between ${ds.radius.md}`,
                    compact ? 'px-2.5 py-1.5' : 'px-3 py-2.5',
                    compact ? ds.type.footnoteLg : ds.type.body,
                    ds.transition.fast,
                    isSelected
                      ? [ds.color.label.link, ds.color.fill.selected].join(' ')
                      : [ds.color.label.primary, ds.color.fill.hover].join(' '),
                  ].join(' ')}
                >
                  <span className="truncate">{option.label}</span>
                  {isSelected && (
                    <svg
                      className={[ds.icon.sm, 'flex-shrink-0 ml-2'].join(' ')}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              );
            })}
            {filteredOptions.length === 0 && (
              <div className={['px-3 py-4 text-center', ds.type.body, ds.color.label.secondary].join(' ')}>
                No results
              </div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
